import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Calendar, Clock } from 'lucide-react';
import { format, addMonths, startOfMonth, endOfMonth, addDays, isSameDay, getDay } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { generateTimeSlots } from '@/lib/appointments';

interface NextAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  customerNameAr?: string;
  customerPhone?: string;
}

interface TimeSlot {
  date: Date;
  coachId: string;
  coachName: string;
  availabilityId: string;
}

export function NextAppointmentDialog({ 
  open, 
  onOpenChange, 
  customerId, 
  customerName,
  customerNameAr,
  customerPhone 
}: NextAppointmentDialogProps) {
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const { currentUser } = useAuth();
  const { language, dir } = useLanguage();

  useEffect(() => {
    if (open && currentUser?.uid) {
      fetchAvailableSlots();
    }
  }, [open, currentUser?.uid]);

  const fetchAvailableSlots = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      
      // Start from 30 days ahead (minimum gap)
      const thirtyDaysAhead = addDays(new Date(), 30);
      const startDate = thirtyDaysAhead;
      const endDate = addMonths(thirtyDaysAhead, 3); // Show 3 months of slots from 30 days ahead
      
      console.log('📅 [NextAppointment] Fetching slots from:', startDate, 'to:', endDate);
      
      // Get approved availabilities for current coach
      const availabilitiesQuery = query(
        collection(db, 'availabilities'),
        where('coachId', '==', currentUser.uid),
        where('status', '==', 'approved')
      );
      
      // Get existing appointments for current coach
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('coachId', '==', currentUser.uid),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate))
      );

      const [availabilitiesSnapshot, appointmentsSnapshot] = await Promise.all([
        getDocs(availabilitiesQuery),
        getDocs(appointmentsQuery)
      ]);

      console.log('📅 [NextAppointment] Found availabilities:', availabilitiesSnapshot.docs.length);
      console.log('📋 [NextAppointment] Found appointments:', appointmentsSnapshot.docs.length);

      // Get all booked slots
      const bookedSlots = appointmentsSnapshot.docs.map(doc => 
        doc.data().date.toDate().getTime()
      );

      const slots: TimeSlot[] = [];

      // Generate available slots for each availability
      availabilitiesSnapshot.docs.forEach(doc => {
        const availability = doc.data();
        if (!availability.startDate || !availability.endDate || !availability.selectedDays) return;

        const availabilityStartDate = availability.startDate.toDate();
        const availabilityEndDate = availability.endDate.toDate();
        
        // Only consider availabilities that extend into our target period
        if (availabilityEndDate < startDate) return;

        try {
          const availabilitySlots = generateTimeSlots(availability, [], startDate);
          
          // Filter slots to be within our target date range and not booked
          const validSlots = availabilitySlots.filter(slotDate => {
            const slotTime = slotDate.getTime();
            return slotDate >= startDate && 
                   slotDate <= endDate && 
                   !bookedSlots.includes(slotTime);
          });

          slots.push(...validSlots.map(date => ({
            date,
            coachId: currentUser.uid,
            coachName: currentUser.displayName || 'Coach',
            availabilityId: doc.id
          })));
        } catch (error) {
          console.error('Error generating slots for availability:', doc.id, error);
        }
      });

      // Sort slots by date
      slots.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      console.log('📅 [NextAppointment] Generated slots:', slots.length);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = async (slot: TimeSlot) => {
    try {
      setCreating(true);
      
      // Validate 30-day minimum gap
      const now = new Date();
      const thirtyDaysFromNow = addDays(now, 30);
      
      if (slot.date < thirtyDaysFromNow) {
        toast.error(language === 'ar' 
          ? 'لا يمكن حجز مواعيد أقل من 30 يوماً من الآن'
          : 'Cannot book appointments less than 30 days from now'
        );
        return;
      }
      
      // Create the appointment
      const appointmentData = {
        customerId,
        coachId: currentUser?.uid,
        customerName,
        customerPhone: customerPhone || '',
        customerNameEn: customerName,
        customerNameAr: customerNameAr || '',
        date: Timestamp.fromDate(slot.date),
        status: 'scheduled',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await addDoc(collection(db, 'appointments'), appointmentData);
      
      toast.success(language === 'ar' ? 'تم حجز الموعد بنجاح' : 'Appointment scheduled successfully');
      onOpenChange(false);
      
      console.log('📅 [NextAppointment] Appointment created:', {
        customerId,
        coachId: currentUser?.uid,
        date: slot.date
      });
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(language === 'ar' ? 'فشل في حجز الموعد' : 'Failed to schedule appointment');
    } finally {
      setCreating(false);
    }
  };

  // Group slots by date for better display
  const groupedSlots = availableSlots.reduce((groups, slot) => {
    const dateKey = format(slot.date, 'yyyy-MM-dd');
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(slot);
    return groups;
  }, {} as Record<string, TimeSlot[]>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]" dir={dir}>
        <DialogHeader>
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {language === 'ar' 
              ? `جدولة الموعد التالي لـ ${customerNameAr || customerName}`
              : `Schedule Next Appointment for ${customerName}`
            }
          </DialogTitle>
          <p className={`text-sm text-muted-foreground ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
            {language === 'ar' 
              ? 'المواعيد متاحة بعد 30 يوماً من الآن'
              : 'Appointments available 30+ days from now'
            }
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">
              {language === 'ar' ? 'جاري تحميل المواعيد المتاحة...' : 'Loading available slots...'}
            </span>
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh]">
            {Object.keys(groupedSlots).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {language === 'ar' 
                  ? 'لا توجد مواعيد متاحة في الشهر القادم'
                  : 'No available slots in the next month'
                }
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedSlots).map(([dateKey, slots]) => (
                  <div key={dateKey} className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(dateKey), 'EEEE, MMMM d, yyyy')}
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((slot, index) => (
                        <Button
                          key={`${dateKey}-${index}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleSlotSelect(slot)}
                          disabled={creating}
                          className="justify-start"
                        >
                          <Clock className="h-3 w-3 mr-2" />
                          {format(slot.date, 'h:mm a')}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
